import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import ImageWithFallback from './ImageWithFallback';
import Reveal from './Reveal';

interface GalleryItem {
  src: string;
  alt: string;
  type: 'image' | 'video';
}

interface GalleryProps {
  items: GalleryItem[];
  title?: string;
  subtitle?: string;
}

const Gallery: React.FC<GalleryProps> = ({ items, title, subtitle }) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const openModal = (index: number) => {
    setSelectedIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedIndex(null);
    document.body.style.overflow = 'unset';
  };

  const goToPrevious = () => {
    if (selectedIndex !== null) {
      setSelectedIndex(selectedIndex === 0 ? items.length - 1 : selectedIndex - 1);
    }
  };

  const goToNext = () => {
    if (selectedIndex !== null) {
      setSelectedIndex(selectedIndex === items.length - 1 ? 0 : selectedIndex + 1);
    }
  };


  return (
    <>
      <section className="py-8 md:py-14 lg:py-20 bg-white relative">
        {/* Cherry Blossom 1 Decoration - Right Side */}
        <img 
          src="/restaurant_photos/cherry_blossom1.png"
          alt=""
          className="absolute right-0 top-[20%] -translate-y-1/2 w-[70%] md:w-[55%] lg:w-[45%] h-auto opacity-10 pointer-events-none z-0"
        />
        
        <div className="section-padding relative z-10">
          {(title || subtitle) && (
            <Reveal className="text-center mb-8 md:mb-12 lg:mb-20">
              {subtitle && (
                <p className="text-luxury-gold text-lg tracking-luxury uppercase font-body mb-4">
                  {subtitle}
                </p>
              )}
              {title && (
                <h2 className="text-display-md md:text-display-lg font-display mb-6 text-luxury-black">
                  {title}
                </h2>
              )}
              <div className="h-px bg-luxury-gold mx-auto w-20" />
            </Reveal>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {items.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: Math.min(index, 8) * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="relative group cursor-pointer overflow-hidden aspect-square"
                onClick={() => openModal(index)}
              >
                {item.type === 'image' ? (
                  <ImageWithFallback
                    src={item.src}
                    alt={item.alt}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                ) : (
                  <div className="relative w-full h-full">
                    <video
                      src={item.src}
                      className="w-full h-full object-cover"
                      muted
                      loop
                      playsInline
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/50 transition-colors duration-300">
                      <Play className="text-white w-12 h-12" />
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-luxury-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {selectedIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
            onClick={closeModal}
          >
            <button
              className="absolute top-8 right-8 text-white/80 hover:text-white transition-colors"
              onClick={closeModal}
            >
              <X size={32} />
            </button>

            <button
              className="absolute left-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
            >
              <ChevronLeft size={40} />
            </button>

            <button
              className="absolute right-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
            >
              <ChevronRight size={40} />
            </button>

            <div
              className="max-w-7xl max-h-[90vh] px-4"
              onClick={(e) => e.stopPropagation()}
            >
              {items[selectedIndex].type === 'image' ? (
                <ImageWithFallback
                  src={items[selectedIndex].src}
                  alt={items[selectedIndex].alt}
                  className="max-w-full max-h-[90vh] object-contain"
                />
              ) : (
                <video
                  src={items[selectedIndex].src}
                  controls
                  autoPlay
                  className="max-w-full max-h-[90vh]"
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Gallery;